import { apiClient } from '../../api/client';
import { sostoyanieRazresheniya } from './browserObservation';

/**
 * Замер местоположения устройства при входе (`s704`, продолжение 05.10.2026).
 *
 * ⛔ СПРАШИВАЕМ БРАУЗЕР, ТОЛЬКО КОГДА СЕРВЕР ОТВЕТИЛ «ДА». Переключатель держит
 * владелец; пока он выключен, сервер отвечает «не спрашивать», и к
 * `navigator.geolocation` этот модуль не прикасается вовсе.
 *
 * ⛔ ОДИН ЗАМЕР, БЕЗ СЛЕЖЕНИЯ. Зовётся `getCurrentPosition` — один раз; `watchPosition`
 * здесь нет и быть не должно. Отказ записывается и повторно не спрашивается.
 * Своего окна с вопросом нет: человек видит только штатный запрос браузера.
 *
 * ⛔ РАБОТА НЕ ЖДЁТ. Всё идёт в фоне после открытия панели; любая ошибка молчит.
 *
 * Если браузер запрос не показал или его закрыли без ответа, панель предлагает
 * кнопку — нажатие человека и есть то действие, которого может требовать браузер.
 */

export type GeoItog = 'none' | 'button';

interface Reshenie { ask: boolean; button: boolean; state: string }

/** Сколько ждём координаты после разрешения. */
const SROK_ZAMERA_MS = 20_000;
/** Сколько ждём ответа человека на запрос браузера, прежде чем записать «не ответили». */
const SROK_OTVETA_MS = 90_000;

const POPYTOK_DOSTAVKI = 3;

async function reshenie(): Promise<Reshenie> {
  const { data } = await apiClient.get('/auth/geolocation');
  return data.data as Reshenie;
}

/** Доставка с тем же ключом попытки: повтор второй строкой на сервере не станет. */
async function dostavit(telo: Record<string, unknown>): Promise<void> {
  for (let n = 0; n < POPYTOK_DOSTAVKI; n += 1) {
    try {
      await apiClient.post('/auth/geolocation', telo);
      return;
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      // Сервер ответил отказом по существу — повторять нечего.
      if (status && status < 500 && status !== 429) return;
      await new Promise((r) => setTimeout(r, 1500 * (n + 1)));
    }
  }
}

const chislo = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

/** Один замер. Возвращает, нужна ли кнопка (браузер не показал запрос или его закрыли). */
async function zamerit(source: 'login' | 'button'): Promise<GeoItog> {
  const telo: Record<string, unknown> = { request_key: crypto.randomUUID(), source };

  if (!('geolocation' in navigator) || typeof navigator.geolocation?.getCurrentPosition !== 'function') {
    await dostavit({ ...telo, result: 'unavailable', reason: window.isSecureContext ? 'unsupported' : 'insecure_context' });
    return 'none';
  }

  const bylo = await sostoyanieRazresheniya('geolocation');

  // Уже запрещено в браузере: вызов ничего не покажет. Записываем отказ и не тревожим.
  if (bylo === 'denied') {
    await dostavit({ ...telo, result: 'denied', reason: 'permission_denied', permission_state: bylo });
    return 'none';
  }

  type Ishod = { pos: GeolocationPosition } | { err: GeolocationPositionError } | { molchanie: true };

  const ishod = await new Promise<Ishod>((gotovo) => {
    const zhdatOtveta = window.setTimeout(() => gotovo({ molchanie: true }), SROK_OTVETA_MS);
    navigator.geolocation.getCurrentPosition(
      (pos) => { window.clearTimeout(zhdatOtveta); gotovo({ pos }); },
      (err) => { window.clearTimeout(zhdatOtveta); gotovo({ err }); },
      { enableHighAccuracy: true, timeout: SROK_ZAMERA_MS, maximumAge: 0 },
    );
  });

  if ('pos' in ishod) {
    const c = ishod.pos.coords;
    await dostavit({
      ...telo,
      result: 'received',
      permission_state: await sostoyanieRazresheniya('geolocation'),
      latitude: c.latitude,
      longitude: c.longitude,
      accuracy: c.accuracy,
      // Чего устройство не знает, браузер отдаёт пустым — так и уходит, без нулей.
      altitude: chislo(c.altitude),
      altitude_accuracy: chislo(c.altitudeAccuracy),
      speed: chislo(c.speed),
      heading: chislo(c.heading),
      measured_at_ms: chislo(ishod.pos.timestamp),
    });
    return 'none';
  }

  const stalo = await sostoyanieRazresheniya('geolocation');

  if ('molchanie' in ishod) {
    await dostavit({ ...telo, result: 'timeout', reason: 'no_answer', permission_state: stalo });
    return 'button';
  }

  const { code, message } = ishod.err;

  if (code === 1) {
    // Запрет страницы настройкой — не решение человека.
    const politika = /permissions? policy|feature policy/i.test(message ?? '');
    // Было «спросит» и осталось «спросит»: запрос закрыли без ответа либо браузер его не показал.
    const zakryt = !politika && bylo === 'prompt' && stalo === 'prompt';
    await dostavit({
      ...telo,
      result: politika ? 'unavailable' : 'denied',
      reason: politika ? 'policy_blocked' : zakryt ? 'prompt_dismissed' : 'permission_denied',
      permission_state: stalo,
    });
    return zakryt ? 'button' : 'none';
  }

  await dostavit({
    ...telo,
    result: code === 3 ? 'timeout' : 'unavailable',
    reason: code === 3 ? 'timeout' : 'position_unavailable',
    permission_state: stalo,
  });
  return 'none';
}

/** После входа: спросить сервер и, если он велел, сделать один замер. */
export async function proveritPriVhode(): Promise<GeoItog> {
  try {
    const r = await reshenie();
    if (r.ask) return await zamerit('login');
    return r.button ? 'button' : 'none';
  } catch {
    return 'none';
  }
}

/** Человек нажал кнопку: ещё одна попытка, если сервер её допускает. */
export async function zamerPoKnopke(): Promise<GeoItog> {
  try {
    const r = await reshenie();
    if (!r.button && !r.ask) return 'none';
    return await zamerit(r.ask ? 'login' : 'button');
  } catch {
    return 'none';
  }
}
